import { useMemo } from 'react';
import { useJournalStore } from '../hooks/useJournalStore';
import type { WidgetProps } from './WidgetRegistry';
import type { Continuation, ContinuationStatus } from '../types';

const STATUS_DOT: Record<ContinuationStatus, string> = {
  created: 'bg-primary',
  running: 'bg-tower-success animate-pulse-slow',
  sleeping: 'bg-tower-warning',
  waiting: 'bg-warning-bg',
  blocked: 'bg-tower-danger',
  merged: 'bg-purpleToken',
  done: 'bg-success-bg',
  killed: 'bg-error',
  failed: 'bg-tower-danger',
};

interface TreeNode {
  continuation: Continuation;
  children: TreeNode[];
}

function buildTree(root: Continuation, descendants: Continuation[]): TreeNode {
  const byParent = new Map<string, Continuation[]>();
  for (const d of descendants) {
    const pid = d.parentId || d.rootId;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid)!.push(d);
  }

  function recurse(c: Continuation): TreeNode {
    const children = (byParent.get(c.id) || []).map(recurse);
    return { continuation: c, children };
  }
  return recurse(root);
}

function TreeNodeRow({ node, depth }: { node: TreeNode; depth: number }) {
  const c = node.continuation;
  return (
    <>
      <div className="flex items-center gap-2 py-1 hover:bg-tower-border/30 px-2 rounded" style={{ paddingLeft: `${depth * 20 + 8}px` }}>
        {depth > 0 && <span className="text-tower-border text-xs">└</span>}
        <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[c.status]}`} />
        <span className="text-xs font-mono text-tower-muted truncate w-16">{c.id.slice(0, 8)}</span>
        <span className="text-xs text-tower-text truncate flex-1">{c.goalFrame.intent}</span>
        <span className="text-xs text-tower-muted font-mono">{c.status}</span>
        <span className="text-xs text-tower-muted font-mono">${c.budget.dollars.spent.toFixed(2)}</span>
      </div>
      {node.children.map((child) => (
        <TreeNodeRow key={child.continuation.id} node={child} depth={depth + 1} />
      ))}
    </>
  );
}

export function LineageTreeWidget(_props: WidgetProps) {
  const journal = useJournalStore((s) => s.activeJournal);

  const tree = useMemo(() => {
    if (!journal) return null;
    return buildTree(journal.rootContinuation, journal.descendants);
  }, [journal]);

  if (!tree) return <div className="text-tower-muted text-sm p-3">No journal loaded</div>;

  return (
    <div className="p-2 overflow-y-auto h-full">
      <TreeNodeRow node={tree} depth={0} />
    </div>
  );
}
